import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { ContextMenuItem, LwButton, Table, useOnScrollDown } from 'redesign';
import { RenderUnsettledUI } from 'shared/components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'ogp/Routes';
import { IconAdd, IconEdit } from 'assets/img';
import { EmployeeResponse } from 'api/services/workforce/workforce-service.types';
import { useIsOgpManager } from 'ogp/hooks/roles/use-is-ogp-manager';
import { useGetEmployeeCertificates } from './hooks/use-get-employee-certificates';
import { getCertificatesColumnDefinition } from './utils/get-certificates-column-definition';
import {
  CertificateTableRow,
  GetEmployeeCertificatesResponse,
  SelectedCertificate,
} from './employee-certificates-tab.types';
import { EditCertificateEndDateModal } from './components/edit-certificate-end-date-modal';

type Props = {
  employeeData: EmployeeResponse;
};

export const EmployeeCertificatesTab = ({ employeeData }: Props) => {
  const certificatesQuery = useGetEmployeeCertificates(employeeData.id);

  if (certificatesQuery.status !== 'success') {
    return <RenderUnsettledUI data={certificatesQuery} />;
  }

  return (
    <EmployeeCertificatesTable
      infiniteQueryResult={certificatesQuery}
      employeeData={employeeData}
    />
  );
};

const EmployeeCertificatesTable = ({
  infiniteQueryResult,
  employeeData,
}: {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<GetEmployeeCertificatesResponse, unknown>;
  employeeData: EmployeeResponse;
}) => {
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isManager = useIsOgpManager();
  const [selectedCertificate, setSelectedCertificate] = useState<SelectedCertificate | null>(null);

  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );

  const handleOpenModal = useCallback((certificate: SelectedCertificate) => {
    setSelectedCertificate(certificate);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCertificate(null);
  }, []);

  const getRowActions = useCallback(
    (
      accreditationId: string,
      _flexWorkerId: string,
      validUntil: string | null,
      endDate: string | null
    ) => {
      const row = flatData.find((item) => item.accreditationId === accreditationId);
      if (!row) {
        return [];
      }

      const certificate: SelectedCertificate = {
        accreditationId,
        certificateName: row.certificateName,
        type: row.type,
        hasFile: row.hasFile,
        validUntil,
        endDate,
      };

      return [
        <ContextMenuItem key="edit" onClick={() => handleOpenModal(certificate)}>
          <IconEdit />
          {t('employee-certificates-tab.edit-end-date', { ns: 'ogp-screen-specific' })}
        </ContextMenuItem>,
      ];
    },
    [handleOpenModal, flatData]
  );

  const columns = useMemo(
    () => getCertificatesColumnDefinition(t, getRowActions),
    [t, getRowActions]
  );

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult,
    flatData,
  });

  const handleAddCertificate = () => {
    navigate(generatePath(routes.EmployeeCertificateAdd, { id: employeeData.id }));
  };

  return (
    <Box maxHeight={'100%'}>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <LwButton
          color="primary"
          startIcon={<IconAdd />}
          onClick={handleAddCertificate}
          iconColorMode="fill"
          disabled={!isManager}
        >
          {t('employee-certificates-tab.add-certificate', { ns: 'ogp-screen-specific' })}
        </LwButton>
      </Box>
      <Table<CertificateTableRow>
        stickyHeader={true}
        columns={columns}
        data={flatData}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
      />
      {isModalOpen && selectedCertificate && (
        <EditCertificateEndDateModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          certificate={{
            accreditationId: selectedCertificate.accreditationId,
            certificateName: selectedCertificate.certificateName,
            certificateType: selectedCertificate.type,
            hasFile: selectedCertificate.hasFile,
            currentEndDate: selectedCertificate.validUntil ?? selectedCertificate.endDate,
          }}
          worker={{
            flexWorkerId: employeeData.id,
            employeeData,
          }}
        />
      )}
    </Box>
  );
};
